import React, { useState, useRef, useContext, useEffect } from "react";
import axios from "axios";
import { StoreContext } from "../context/StoreContext";
import { TailSpin } from "react-loader-spinner";

const OtpAuthDialog = ({ isOpen, onClose, onSuccess }) => {
  const { deviceId, setUserId, setAccessToken } = useContext(StoreContext);
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (isOpen) {
      setStep("phone");
      setPhone("");
      setOtp("");
      setError("");
      setResendCooldown(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const sendOtp = async () => {
    await axios.post(
      "/api/userservice/authentication/generate-otp/v2",
      { phone, deviceId },
      { headers: { "Content-Type": "application/json" } }
    );
  };

  const handleSendOtp = async () => {
    if (phone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await sendOtp();
      setStep("otp");
      setResendCooldown(30);
      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (err) {
      setError("Failed to send OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        "/api/userservice/authentication/verify-otp",
        { type: "OTP", userType: "CUSTOMER", phone, otp },
        { headers: { "Content-Type": "application/json" } }
      );
      const payload = response?.data;
      const data = payload?.data || payload;
      if (data?.userId && data?.accessToken) {
        setUserId(data.userId);
        setAccessToken(data.accessToken);
        onSuccess?.();
      } else {
        setError("Verification failed. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Invalid OTP. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setLoading(true);
    setError("");
    try {
      await sendOtp();
      setOtp("");
      setResendCooldown(30);
    } catch (err) {
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setStep("phone");
    setOtp("");
    setError("");
    setResendCooldown(0);
    setTimeout(() => phoneInputRef.current?.focus(), 100);
  };

  const handleClose = () => {
    setStep("phone");
    setPhone("");
    setOtp("");
    setError("");
    setResendCooldown(0);
    onClose();
  };

  return (
    <div
      role="dialog"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
    >
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {step === "phone" ? (
          <>
            <h2 className="text-xl font-semibold text-[#171725] mb-1">
              Login to continue
            </h2>
            <p className="text-sm text-[#8F8F97] mb-6">
              Verify your mobile number to complete your order
            </p>

            <div className="flex border border-gray-300 rounded-xl px-3 py-3 items-center focus-within:ring-2 focus-within:ring-[#FBC02D] focus-within:border-transparent transition">
              <span className="pr-2 text-[#171725] font-medium">+91</span>
              <input
                ref={phoneInputRef}
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (val.length <= 10) {
                    setPhone(val);
                    setError("");
                  }
                }}
                onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                placeholder="Enter mobile number"
                className="w-full border-none focus:outline-none tracking-wide text-lg"
              />
            </div>

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

            <button
              onClick={handleSendOtp}
              disabled={loading || phone.length !== 10}
              className={`w-full bg-[#FBC02D] text-white text-lg py-3 mt-4 rounded-xl font-semibold transition-colors ${
                loading || phone.length !== 10
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-[#e5ad28] active:bg-[#d9a020]"
              }`}
            >
              {loading ? (
                <span className="flex justify-center">
                  <TailSpin
                    height="24"
                    width="24"
                    color="white"
                    ariaLabel="loading"
                  />
                </span>
              ) : (
                "Send OTP"
              )}
            </button>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-[#171725] mb-1">
              Enter OTP
            </h2>
            <p className="text-sm text-[#8F8F97] mb-6">
              We sent a code to{" "}
              <span className="text-[#171725] font-semibold">
                +91 {phone}
              </span>
            </p>

            <input
              ref={otpInputRef}
              type="tel"
              inputMode="numeric"
              value={otp}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                if (val.length <= 6) {
                  setOtp(val);
                  setError("");
                }
              }}
              onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
              placeholder="------"
              className="border border-gray-300 rounded-xl text-2xl px-3 py-3 w-full tracking-[0.4em] text-center focus:outline-none focus:ring-2 focus:ring-[#FBC02D] focus:border-transparent transition"
              maxLength={6}
            />

            <div className="flex justify-between mt-3">
              <button
                onClick={handleChangeNumber}
                className="text-[#8F8F97] text-sm hover:text-[#171725] transition-colors"
              >
                Change Number
              </button>
              <button
                onClick={handleResendOtp}
                disabled={loading || resendCooldown > 0}
                className={`text-sm font-semibold transition-colors ${
                  resendCooldown > 0
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-[#FBC02D] hover:text-[#e5ad28]"
                }`}
              >
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend OTP"}
              </button>
            </div>

            {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

            <button
              onClick={handleVerifyOtp}
              disabled={loading || otp.length !== 6}
              className={`w-full bg-[#FBC02D] text-white text-lg py-3 mt-4 rounded-xl font-semibold transition-colors ${
                loading || otp.length !== 6
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-[#e5ad28] active:bg-[#d9a020]"
              }`}
            >
              {loading ? (
                <span className="flex justify-center">
                  <TailSpin
                    height="24"
                    width="24"
                    color="white"
                    ariaLabel="loading"
                  />
                </span>
              ) : (
                "Verify & Proceed"
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OtpAuthDialog;
